import { fontFamily, Policy } from '@/types/tower/Tower';
import { DEFAULT_STROKE_WIDTH, DEFAULT_TEXT_STYLE } from '@/utilities/tower/v2/Canvas';
import { fabric } from 'fabric';
import LabeledObjectBase from './LabeledObjectBase';
import { numToMoneyString } from '@/utilities/Money';
import { compactNumber, percentage } from '@/utilities/Number';
import Store from '@/store';

export const DEFAULT_TEXT_COLOR = '#FFFFFF';
export const TYPE = 'labeled-rect';

const LabeledRect = fabric.util.createClass(LabeledObjectBase, {
  type: TYPE,
  snappable: true,
  label: undefined as fabric.Textbox | undefined,

  initialize: function(options: fabric.RectOptions | any) {
    options = options || {};

    const objects: fabric.Object[] = [];

    this.textColor = options.textColor ?? DEFAULT_TEXT_COLOR;

    if (options.objects) {
      objects[0] = new fabric.Rect(options.objects[0]);
      objects[1] = new fabric.Textbox('', options.objects[1]);
    } else {
      objects[0] = new fabric.Rect({
        originX: 'center',
        originY: 'center',
        fill: '#FF0000',
        opacity: 0.5,
        stroke: '#61BABA',
        strokeWidth: DEFAULT_STROKE_WIDTH,
        strokeDashArray: [0],
        strokeUniform: true,
        paintFirst: 'stroke',
      });

      objects[1] = new fabric.Textbox('', {
        fontSize: 18,
        originX: 'center',
        originY: 'center',
        textAlign: 'center',
        fill: DEFAULT_TEXT_COLOR,
        strokeDashArray: [0],
        strokeUniform: true,
        lockScalingX: true,
        lockScalingY: true,
      });

      this.textStyle = DEFAULT_TEXT_STYLE;
      objects[1].fontFamily = fontFamily(this.textStyle);
    }

    this.callSuper('initialize', objects, {...options, strokeUniform: true});

    this.on('mousedblclick', () => {
      this.editLabel();
    });
  },

  toObject: function() {
    return fabric.util.object.extend(this.callSuper('toObject'), {
      id: this.id,
      policy: this.policy,
      lockScalingFlip: this.lockScalingFlip,
      textColor: this.textColor,
    });
  },

  setLabel: function(text: string) {
    this._objects[1].set('text', text);
  },

  editLabel: function() {
    this._objects[1].clone((clonedObj: fabric.Object) => {
      this._objects[1].set('visible', false);

      this.label = clonedObj;
      this.label.setPositionByOrigin(this.getCenterPoint(), 'center', 'center');
      this.label.set({
        textColor: DEFAULT_TEXT_COLOR, 
        scaleX: 1,
        scaleY: 1,
        angle: this.angle,
        width: this._objects[1].width,
        height: this._objects[1].height,
        lockMovementX: true,
        lockMovementY: true,
        lockScalingX: true,
        lockScalingY: true,
      });

      this.canvas.add(this.label);
      this.canvas.setActiveObject(this.label);
      this.label.enterEditing();

      this.label.on('editing:exited', () => {
        if (this.label.text !== this._objects[1].text) {
          delete this.policy; // custom label set, stop letting the display options update the label
        }

        this._objects[1].set('text', this.label.text);
        this.canvas.remove(this.label);
        delete this.label;

        this._objects[1].set('visible', true);
      });
    });
  },

  updateLabelFromPolicy: function() {
  const displayOptions = Store.state.towerCanvasState.displayOptions;
  const policy = this.policy as Policy;
  if(policy && displayOptions) {
    const labelParts: string[] = [];

    if (displayOptions.carrier?.enabled) {
      if (policy.carrierGroup?.name) {
        labelParts.push(policy.carrierGroup?.name ?? '');
      } else {
        labelParts.push('--');
      }
    }

    if (displayOptions.aggregateLimit?.enabled) {
      if (policy.aggregateLimit) {
        let label = `${compactNumber(policy.aggregateLimit)}`;

        if (policy.quotaShareTotal && policy.quotaShareTotal !== 0) {
          if(displayOptions.quotaSharePercent?.enabled){
            label = `${percentage((policy.aggregateLimit / policy.quotaShareTotal) * 100 )}`;
          }

          label += ` p/o ${compactNumber(policy.quotaShareTotal)}`;
        }

        if (policy.attachmentPoint) {
          label += ( ` xs ${compactNumber(policy.attachmentPoint)}`);
        }

        labelParts.push(label);
      } else {
        labelParts.push('--');
      }
    }

    if (displayOptions.occurrenceLimit?.enabled) {
      if (policy.occurrenceLimit) {
        let label = `${compactNumber(policy.occurrenceLimit)}`;

        if (policy.quotaShareTotal && policy.quotaShareTotal !== 0) {
          if(displayOptions.quotaSharePercent?.enabled){
            label = `${percentage((policy.occurrenceLimit / policy.quotaShareTotal) * 100 )}`;
          }

          label += ` p/o ${compactNumber(policy.quotaShareTotal)}`;
        }

        if (policy.attachmentPoint) {
          label += ( ` xs ${compactNumber(policy.attachmentPoint)}`);
        }

        labelParts.push(label);
      } else {
        labelParts.push('--');
      }
    }

    if (displayOptions.aggregate?.enabled) {
      if (policy.premium && policy.aggregateLimit) {
        labelParts.push(`${percentage(((policy.premium || 0) / policy.aggregateLimit) * 100)}`);
      } else {
        labelParts.push('--');
      }
    }

    if (displayOptions.occurrence?.enabled) {
      if (policy.premium && policy.occurrenceLimit) {
        labelParts.push(`${percentage(((policy.premium || 0) / policy.occurrenceLimit) * 100)}`);
      } else {
        labelParts.push('--');
      }
    }

    if (displayOptions.premium?.enabled) {
      if (policy.premium) {
        labelParts.push(numToMoneyString(policy.premium));
      } else {
        labelParts.push('--');
      }
    }

    this.setLabel(labelParts.join('\n'));
  }
}
});

LabeledRect.fromObject = function(object: fabric.Object, callback: (obj: fabric.Object) => void) {
  const labelRect = new LabeledRect(object);
  if (callback) callback(labelRect);
  return labelRect;
};

export default LabeledRect;
